import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search, SlidersHorizontal, X, ChevronLeft, ChevronRight, AlertCircle, RefreshCcw, ArrowUpDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Product } from '@/types';
import { formatPrice, truncateText, getDomain } from '@/lib/utils';
import { calculateProductDiscount } from '@/lib/discount';
import { DiscountBadge } from '@/components/ui/discount-badge';
import { ProductGridSkeleton } from '@/components/ui/skeleton';
import { NoImagePlaceholder } from '@/components/ui/no-image-placeholder';

const PRODUCTS_PER_PAGE = 12;

export function ModernProductsPage() {
  const [searchParams] = useSearchParams();
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState<string>(searchParams.get('category') || '');
  const [sortBy, setSortBy] = useState<'price-asc' | 'price-desc' | 'name'>('name');
  const [showFilters, setShowFilters] = useState(false);
  const [showSortModal, setShowSortModal] = useState(false);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      const domain = getDomain();
      const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/api/v1/products/visible?domain=${domain}`);
      
      if (!response.ok) {
        throw new Error('Failed to connect to server. Please check your connection and try again.');
      }
      
      const data = await response.json();
      if (!data.status) {
        throw new Error(data.message || 'Failed to load products. Please try again.');
      }
      
      const filteredProducts = data.products.filter((p: Product) => 
        p.status === 1 && p.totalStock > 0
      );
      setAllProducts(filteredProducts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load products. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, sortBy]);

  const categories = [...new Set(allProducts.map(p => p.category))].filter(Boolean);

  const filteredProducts = allProducts
    .filter(product => 
      (product.produt_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.category && product.category.toLowerCase().includes(searchTerm.toLowerCase()))) &&
      (!selectedCategory || product.category === selectedCategory)
    )
    .sort((a, b) => {
      switch (sortBy) {
        case 'price-asc':
          return parseInt(a.sellingprice) - parseInt(b.sellingprice);
        case 'price-desc':
          return parseInt(b.sellingprice) - parseInt(a.sellingprice);
        default:
          return a.produt_name.localeCompare(b.produt_name);
      }
    });

  const totalPages = Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE);
  const startIndex = (currentPage - 1) * PRODUCTS_PER_PAGE;
  const endIndex = startIndex + PRODUCTS_PER_PAGE;
  const currentProducts = filteredProducts.slice(startIndex, endIndex);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSortBy('name');
  };

  return (
    <>
      <Helmet>
        <title>Products - Store</title>
        <meta name="description" content="Browse our collection of products" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 pb-24 md:pb-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-3xl font-display font-bold text-gray-900 mb-2">
              All Products
            </h1>
          </div>

          {/* Search and Filters */}
          <div className="sticky top-16 z-30 bg-gray-50 pb-4">
            <div className="bg-white rounded-2xl shadow-soft p-4">
              {/* Search Bar */}
              <div className="relative mb-4">
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input-field pl-12"
                />
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-100"
                  >
                    <X className="h-4 w-4 text-gray-400" />
                  </button>
                )}
              </div>

              {/* Filter Toggle Button (Mobile) */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="md:hidden w-full flex items-center justify-center gap-2 py-3 px-4 bg-brand-50 text-brand-600 rounded-xl font-medium hover:bg-brand-100 transition-colors"
              >
                <SlidersHorizontal className="h-5 w-5" />
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </button>

              {/* Filters */}
              <AnimatePresence>
                {(showFilters || window.innerWidth >= 768) && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Category
                        </label>
                        <select
                          value={selectedCategory}
                          onChange={(e) => setSelectedCategory(e.target.value)}
                          className="input-field"
                        >
                          <option value="">All Categories</option>
                          {categories.map((category) => (
                            <option key={category} value={category}>
                              {category}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Sort By
                        </label>
                        <select
                          value={sortBy}
                          onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                          className="input-field"
                        >
                          <option value="name">Name (A-Z)</option>
                          <option value="price-asc">Price: Low to High</option>
                          <option value="price-desc">Price: High to Low</option>
                        </select>
                      </div>

                      <div className="flex items-end">
                        <button
                          onClick={clearFilters}
                          className="w-full py-3 px-4 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                        >
                          Clear Filters
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Products Grid */}
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <ProductGridSkeleton count={PRODUCTS_PER_PAGE} />
            </div>
          ) : error ? (
            <div className="bg-white rounded-2xl shadow-soft p-8 text-center">
              <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
              <p className="text-gray-600 mb-6">{error}</p>
              <button
                onClick={fetchProducts}
                className="inline-flex items-center gap-2 btn-primary"
              >
                <RefreshCcw className="h-4 w-4" />
                Try Again
              </button>
            </div>
          ) : currentProducts.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-soft p-12 text-center">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No products found</h3>
              <p className="text-gray-600 mb-6">Try adjusting your search or filters</p>
              <button
                onClick={clearFilters}
                className="btn-primary"
              >
                Clear All Filters
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
                {currentProducts.map((product, index) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="product-card"
                  >
                    <Link
                      to={`/product/${product.id}`}
                      className="group block card"
                    >
                      <div className="aspect-square relative overflow-hidden">
                        {product.images && product.images.length > 0 ? (
                          <img
                            src={product.images[0]}
                            alt={product.produt_name}
                            className="w-full h-full object-cover transform transition-transform duration-300 group-hover:scale-105"
                          />
                        ) : (
                          <NoImagePlaceholder 
                            className="w-full h-full rounded-none" 
                            size="lg"
                            text="No Image"
                          />
                        )}
                        {product.postType === "1" && (
                          <div className="absolute top-2 right-2 bg-accent-primary text-white px-3 py-1 rounded-full text-xs font-semibold">
                            Pre-Order
                          </div>
                        )}
                        {(() => {
                          const discountInfo = calculateProductDiscount(product, 1);
                          return discountInfo.hasDiscount && (
                            <div className="absolute top-2 left-2">
                              <DiscountBadge 
                                discountType={discountInfo.discountType}
                                discountAmount={discountInfo.discountAmount}
                              />
                            </div>
                          );
                        })()}
                      </div>
                      <div className="p-4">
                        <h3 className="font-medium text-gray-900 mb-1 group-hover:text-brand-600 transition-colors line-clamp-2">
                          {truncateText(product.produt_name, 50)}
                        </h3>
                        <p className="text-xs text-gray-500 mb-2">
                          {product.category || 'Uncategorized'}
                        </p>
                        <div className="flex justify-between items-center">
                          <span className="text-lg font-bold text-brand-600">
                            {formatPrice(product.sellingprice)}
                          </span>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-xl hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-10 h-10 rounded-xl font-medium transition-all ${
                            currentPage === pageNum
                              ? 'bg-brand-500 text-white shadow-md'
                              : 'hover:bg-white text-gray-700'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-xl hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Mobile Bottom Action Bar */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40 md:hidden">
          <div className="grid grid-cols-2 gap-2 p-3">
            <button
              onClick={() => setShowSortModal(true)}
              className="flex items-center justify-center gap-2 py-3 bg-white border-2 border-gray-200 text-gray-700 font-semibold rounded-xl transition-colors hover:bg-gray-50"
            >
              <ArrowUpDown className="h-5 w-5" />
              Sort
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center justify-center gap-2 py-3 bg-brand-500 hover:bg-brand-600 text-white font-semibold rounded-xl transition-colors"
            >
              <SlidersHorizontal className="h-5 w-5" />
              Filter
            </button>
          </div>
        </div>

        {/* Sort Modal - Mobile */}
        <AnimatePresence>
          {showSortModal && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setShowSortModal(false)}
                className="fixed inset-0 bg-black/50 z-50 md:hidden"
              />
              <motion.div
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 z-50 shadow-2xl md:hidden"
              >
                <h3 className="text-lg font-bold text-gray-900 mb-4">Sort By</h3>
                <div className="space-y-2">
                  {[
                    { value: 'name', label: 'Name (A-Z)' },
                    { value: 'price-asc', label: 'Price: Low to High' },
                    { value: 'price-desc', label: 'Price: High to Low' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setSortBy(option.value as typeof sortBy);
                        setShowSortModal(false);
                      }}
                      className={`w-full text-left px-4 py-3 rounded-xl font-medium transition-colors ${
                        sortBy === option.value
                          ? 'bg-brand-50 text-brand-600 border-2 border-brand-500'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}


